// ========== CHART CONSTANTS & CONFIGURATION ==========
import {
  MousePointer, TrendingUp, Minus, Crosshair, Type, Square,
  ArrowRight, Hash, Maximize2, GitBranch, Triangle, PenTool,
  ArrowUpRight, MessageSquare, Ruler, Clock
} from 'lucide-react';

// ===== TIMEFRAMES =====
export const TIMEFRAMES = [
  { label: '1D',  value: '1d'  },
  { label: '5D',  value: '5d'  },
  { label: '1M',  value: '1mo' },
  { label: '3M',  value: '3m'  },
  { label: '6M',  value: '6m'  },
  { label: 'YTD', value: 'ytd' },
  { label: '1Y',  value: '1y'  },
  { label: '3Y',  value: '3y'  },
  { label: '5Y',  value: '5y'  },
  { label: 'All', value: 'max' },
];

// ===== CHART TYPES =====
export const CHART_TYPE_GROUPS = [
  {
    label: 'Candlestick',
    types: [
      { label: 'Bars', value: 'bars' },
      { label: 'Candles', value: 'candlestick' },
      { label: 'Hollow Candles', value: 'hollowcandle' },
      { label: 'Volume Candles', value: 'volumecandles' },
    ],
  },
  {
    label: 'Line',
    types: [
      { label: 'Line', value: 'line' },
      { label: 'Line with Markers', value: 'linemarkers' },
      { label: 'Step Line', value: 'stepline' },
    ],
  },
  {
    label: 'Area',
    types: [
      { label: 'Area', value: 'area' },
      { label: 'HLC Area', value: 'hlcarea' },
      { label: 'Baseline', value: 'baseline' },
    ],
  },
  {
    label: 'Other',
    types: [
      { label: 'Columns', value: 'columns' },
      { label: 'High-Low', value: 'highlow' },
    ],
  },
  {
    label: 'Advanced',
    types: [
      { label: 'Heikin Ashi', value: 'heikinashi' },
      { label: 'Renko', value: 'renko' },
      { label: 'Line Break', value: 'linebreak' },
      { label: 'Kagi', value: 'kagi' },
      { label: 'Point & Figure', value: 'pointfigure' },
      { label: 'Range Bars', value: 'rangebars' },
    ],
  },
];

export const CHART_TYPES = CHART_TYPE_GROUPS.flatMap(g => g.types);

// ===== INDICATORS =====
export const INDICATORS = {
  overlay: [
    { label: 'SMA 20', value: 'sma20' },
    { label: 'SMA 50', value: 'sma50' },
    { label: 'SMA 200', value: 'sma200' },
    { label: 'EMA 20', value: 'ema20' },
    { label: 'Bollinger Bands', value: 'bollinger' },
    { label: 'Volume', value: 'volume' },
    { label: 'VWAP', value: 'vwap' },
    { label: 'Parabolic SAR', value: 'psar' },
    { label: 'SuperTrend', value: 'supertrend' },
    { label: 'Keltner Channels', value: 'keltner' },
    { label: 'Donchian Channels', value: 'donchian' },
  ],
  momentum: [
    { label: 'RSI (14)', value: 'rsi' },
    { label: 'MACD (12,26,9)', value: 'macd' },
    { label: 'Stochastic (14,3)', value: 'stochastic' },
    { label: 'ADX (14)', value: 'adx' },
    { label: 'MFI (14)', value: 'mfi' },
  ],
  volatility: [
    { label: 'ATR (14)', value: 'atr' },
    { label: 'Std Dev Bands', value: 'stddev' },
    { label: 'Hist. Volatility', value: 'hvol' },
  ],
  volume: [
    { label: 'OBV', value: 'obv' },
    { label: 'VWAP Bands (2σ)', value: 'vwapbands' },
    { label: 'A/D Line', value: 'adline' },
    { label: 'Chaikin MF (20)', value: 'cmf' },
  ],
  orderFlow: [
    { label: 'Delta (Buy-Sell)', value: 'delta' },
    { label: 'Cumulative Delta', value: 'cumdelta' },
    { label: 'Volume Profile', value: 'volprofile' },
  ],
};

export const ALL_INDICATORS = [
  ...INDICATORS.overlay, ...INDICATORS.momentum,
  ...INDICATORS.volatility, ...INDICATORS.volume, ...INDICATORS.orderFlow,
];

// ===== DRAWING TOOLS =====
export const DRAWING_TOOLS = {
  selection: [
    { label: 'Cursor', value: 'cursor', icon: MousePointer },
    { label: 'Crosshair', value: 'crosshair', icon: Crosshair },
  ],
  lines: [
    { label: 'Trendline', value: 'trendline', icon: TrendingUp },
    { label: 'Horizontal', value: 'horizontal', icon: Minus },
    { label: 'Vertical', value: 'vertical', icon: Hash },
    { label: 'Rectangle', value: 'rectangle', icon: Square },
    { label: 'Ray', value: 'ray', icon: ArrowRight },
    { label: 'Extended Line', value: 'extended', icon: Maximize2 },
    { label: 'Channel', value: 'channel', icon: GitBranch },
  ],
  fibonacci: [
    { label: 'Fib Retrace', value: 'fibonacci', icon: Crosshair },
    { label: 'Fib Extension', value: 'fibext', icon: TrendingUp },
    { label: 'Fib Fan', value: 'fibfan', icon: Triangle },
    { label: 'Fib Arc', value: 'fibarc', icon: Crosshair },
    { label: 'Fib Time', value: 'fibtime', icon: Clock },
    { label: 'Fib Channel', value: 'fibchannel', icon: GitBranch },
  ],
  gann: [
    { label: 'Gann Fan', value: 'gannfan', icon: Triangle },
    { label: 'Gann Square', value: 'gannsquare', icon: Square },
    { label: 'Gann Grid', value: 'ganngrid', icon: Hash },
  ],
  elliott: [
    { label: 'Impulse', value: 'elliott_impulse', icon: TrendingUp },
    { label: 'Corrective', value: 'elliott_corrective', icon: TrendingUp },
    { label: 'Projection', value: 'elliott_projection', icon: ArrowUpRight },
  ],
  patterns: [
    { label: 'H&S', value: 'pat_hs', icon: TrendingUp },
    { label: 'Double T/B', value: 'pat_double', icon: TrendingUp },
    { label: 'Triangle', value: 'pat_triangle', icon: Triangle },
    { label: 'Wedge', value: 'pat_wedge', icon: Triangle },
    { label: 'Flag', value: 'pat_flag', icon: Square },
  ],
  advanced: [
    { label: 'Pitchfork', value: 'pitchfork', icon: PenTool },
    { label: 'Reg. Channel', value: 'regchannel', icon: GitBranch },
    { label: 'Price Range', value: 'pricerange', icon: Ruler },
    { label: 'Date Range', value: 'daterange', icon: Clock },
    { label: 'Arrow', value: 'arrow', icon: ArrowUpRight },
    { label: 'Text', value: 'text', icon: Type },
    { label: 'Callout', value: 'callout', icon: MessageSquare },
  ],
};

export const ALL_DRAWING_TOOLS = [
  ...DRAWING_TOOLS.selection, ...DRAWING_TOOLS.lines,
  ...DRAWING_TOOLS.fibonacci, ...DRAWING_TOOLS.gann,
  ...DRAWING_TOOLS.elliott, ...DRAWING_TOOLS.patterns,
  ...DRAWING_TOOLS.advanced,
];

// ===== DRAWING COLORS =====
export const DRAWING_COLORS = [
  '#2962FF', '#00BCD4', '#FF5252', '#FF9800',
  '#9C27B0', '#FFFFFF', '#FFEB3B', '#00E5FF',
];

// ===== LINE WIDTHS =====
export const LINE_WIDTHS = [1, 2, 3, 4, 5];

// ===== COMPARISON MODES =====
export const COMPARISON_MODES = [
  { label: 'Overlay', value: 'overlay' },
  { label: 'Relative', value: 'relative' },
  { label: 'Spread', value: 'spread' },
  { label: 'Ratio', value: 'ratio' },
];

// ===== LAYOUT OPTIONS =====
export const LAYOUT_OPTIONS = [
  { label: '1×1', value: 1 },
  { label: '2H', value: 2 },
  { label: '2V', value: 3 },
  { label: '2×2', value: 4 },
];

// ===== TICKER PRESETS =====
export const TICKER_PRESETS = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'TSLA', 'META'];

// ===== THEMES =====
export const THEMES = {
  dark: {
    bg: '#131722', panel: '#1e222d', border: '#2a2e39', text: '#d1d4dc',
    textMuted: '#787b86', accent: '#2962FF', up: '#26a69a', down: '#ef5350',
    gridLines: '#1e222d33',
  },
  light: {
    bg: '#ffffff', panel: '#f8f9fa', border: '#e0e3eb', text: '#131722',
    textMuted: '#787b86', accent: '#2962FF', up: '#26a69a', down: '#ef5350',
    gridLines: '#e0e3eb33',
  },
};

// ===== KEYBOARD SHORTCUTS =====
export const KEYBOARD_SHORTCUTS = [
  { key: '?', desc: 'Show shortcuts' },
  { key: 'T', desc: 'Toggle theme' },
  { key: 'I', desc: 'Indicators panel' },
  { key: 'A', desc: 'AI Analysis' },
  { key: 'R', desc: 'Risk Calculator' },
  { key: 'B', desc: 'Alerts panel' },
  { key: 'J', desc: 'Trade Journal' },
  { key: 'P', desc: 'Performance' },
  { key: 'D', desc: 'Drawing tools' },
  { key: 'C', desc: 'Comparison' },
  { key: 'W', desc: 'Watchlist' },
  { key: 'G', desc: 'Go to date' },
  { key: 'F', desc: 'Fit content' },
  { key: 'Space', desc: 'Play/Pause replay' },
  { key: 'Esc', desc: 'Close panels / Back' },
  { key: '1-9', desc: 'Switch timeframe' },
  { key: '+/-', desc: 'Zoom in/out' },
  { key: 'Ctrl+Z', desc: 'Undo drawing' },
  { key: 'Ctrl+Y', desc: 'Redo drawing' },
  { key: 'Ctrl+S', desc: 'Screenshot' },
  { key: 'Del', desc: 'Delete drawing' },
  { key: '←/→', desc: 'Step replay' },
];

// ===== ALERT SOUNDS =====
export const ALERT_SOUNDS = [
  { label: 'Beep', value: 'beep', freq: 800, type: 'sine' },
  { label: 'Chime', value: 'chime', freq: 523, type: 'triangle' },
  { label: 'Alert', value: 'alert', freq: 440, type: 'square' },
];

// ===== COMPARISON COLORS =====
export const COMPARISON_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
